import React from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { buildTheme, ColorMode } from './index';
import { useBranding, TenantBranding } from '../hooks/useBranding';

type Props = { children: React.ReactNode };

export default function ThemeProvider({ children }: Props) {
  const [mode, setModeState] = React.useState<ColorMode>(() => (localStorage.getItem('ui_mode') as ColorMode) || 'light');
  const { branding, loading, refresh: refreshBranding } = useBranding();

  const theme = React.useMemo(() => buildTheme(mode, branding), [mode, branding]);

  const setMode = React.useCallback((next: ColorMode) => {
    localStorage.setItem('ui_mode', next);
    setModeState(next);
  }, []);

  const toggleMode = React.useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  // Stable context value — only a new object when mode/toggleMode/branding actually change.
  // Without this, every ThemeProvider re-render creates a new value reference and forces
  // all ModeContext consumers to re-render even when nothing meaningful changed.
  const contextValue = React.useMemo(
    () => ({ mode, setMode, toggleMode, branding, refreshBranding }),
    [mode, setMode, toggleMode, branding, refreshBranding],
  );

  // Show loading spinner while fetching branding
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ModeContext.Provider value={contextValue}>
        {children}
      </ModeContext.Provider>
    </MuiThemeProvider>
  );
}

interface ModeContextValue {
  mode: ColorMode;
  setMode: (next: ColorMode) => void;
  toggleMode: () => void;
  branding: TenantBranding | null;
  refreshBranding: () => void;
}

export const ModeContext = React.createContext<ModeContextValue>({
  mode: 'light',
  setMode: () => {},
  toggleMode: () => {},
  branding: null,
  refreshBranding: () => {},
});
